/**
 * Session bootstrap.
 * Fetches a per-page-load session id from the public `/sessions/v1` endpoint
 * once on startup. The id is attached to all subsequent Jig API requests via
 * an axios request interceptor.
 *
 * Design notes:
 * - The bootstrap POST is unauthenticated and runs in parallel with the Auth0
 *   token exchange, so first authenticated requests don't pay the round-trip.
 * - The id is read from the exposed `X-Jig-Session-Id` response header so
 *   large session ids are never parsed as JSON numbers.
 *
 * - On failure we resolve the ready promise with `''` so consumers don't hang;
 *   requests proceed without the header and a warning is logged.
 */
import { AppConst, UtilsConst } from '@/constants'
import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'

declare var consoleWarn: any

let sessionId: string = ''
let readyPromise: Promise<string> | null = null

function readSessionIdFromResponseHeaders(headers: any): string {
  if (!headers) {
    return ''
  }
  const headerName = UtilsConst.RequestHeaders.CustomHeaderKeySessionId
  const lowerHeaderName = headerName.toLowerCase()
  let value = ''

  if (typeof headers.get === 'function') {
    value = headers.get(headerName) || headers.get(lowerHeaderName) || ''
  }
  if (!value) {
    value = headers[headerName] || headers[lowerHeaderName] || ''
  }
  if (Array.isArray(value)) {
    value = value[0] || ''
  }
  return typeof value === 'string' ? value.trim() : String(value)
}

export function initSession(): Promise<string> {
  if (readyPromise) {
    return readyPromise
  }

  const url = AppConst.apiDomain + AppConst.APIEndpoint.sessionsV1Url
  readyPromise = axios
    .post(url, undefined, {
      headers: { 'Content-Type': 'application/json' },
    })
    .then((response) => {
      const id = readSessionIdFromResponseHeaders(response && response.headers)
      if (!id) {
        consoleWarn('Session bootstrap response missing X-Jig-Session-Id')
        sessionId = ''
        return ''
      }
      sessionId = id
      return id
    })
    .catch((error) => {
      consoleWarn('Session bootstrap failed; proceeding without X-Jig-Session-Id', error)
      sessionId = ''
      return ''
    })

  return readyPromise
}

export function getSessionId(): string {
  return sessionId
}

export function whenSessionReady(): Promise<string> {
  return readyPromise || Promise.resolve('')
}

/**
 * Adds the per-page session id to JigAPI requests sent through the shared Axios
 * client. The session bootstrap request and isolated public-asset clients are
 * excluded; public thumbnail downloads intentionally remain untracked to avoid
 * a CORS preflight.
 */
export async function attachSessionIdToJigApiRequest(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  const url = config.url || ''
  const baseURL = config.baseURL || ''
  const isJigApi =
    (baseURL && baseURL.indexOf(AppConst.apiDomain) === 0) ||
    url.indexOf(AppConst.apiDomain) === 0
  if (!isJigApi) {
    return config
  }

  const isSessionBootstrap =
    url.indexOf(AppConst.APIEndpoint.sessionsV1Url) !== -1 ||
    (baseURL + url).indexOf(AppConst.APIEndpoint.sessionsV1Url) !== -1
  if (isSessionBootstrap) {
    return config
  }

  const sid = await whenSessionReady()
  if (sid) {
    config.headers = config.headers || ({} as InternalAxiosRequestConfig['headers'])
    config.headers[UtilsConst.RequestHeaders.CustomHeaderKeySessionId] = sid
  }
  return config
}

export function _resetSessionForTests(): void {
  sessionId = ''
  readyPromise = null
}
